// hud-i18n.ts — locale-aware HUD copy for the Multi Internet instrument.
//
// The animation layer was ported with its copy hardcoded in English
// (`HUD_STRINGS_EN`), so the diagram stayed English on every locale (SCRUM-1872).
// This module overlays the translated subset onto that constant instead of
// replacing it: the base object keeps supplying every other section, and a
// missing translation key degrades to the English string rather than to an empty
// label inside an SVG.
//
// What gets translated is the copy a visitor reads as prose or as a name --
// headings, narration, screen-reader labels, swipe instructions. What stays in
// English is the instrument's telemetry, which reads as a readout rather than as
// language: `ONLINE · LIVE`, `INTERRUPTED`, `NO SIGNAL`, `CONNECTED`,
// `ROUTE GROUP 01`, `9 ROUTES LIVE`, `1 FORWARDED`, `3 / 3`, the per-scenario
// `indicator` badges, and the brand names (`FINAL NOPING`, `FORTNITE`). That line
// is a product decision, not a technical limit -- moving a token across it is a
// matter of adding or removing a key below.
import type { TFunction } from 'next-i18next';

import type { HudStrings, MultiInternetGuideStage, MultiInternetScenarioKey } from './strings';
import { HUD_STRINGS_EN } from './strings';

const SCENARIO_KEYS: MultiInternetScenarioKey[] = ['restored', 'i1', 'i2', 'i3', 'i12', 'i13', 'i23'];

function read(t: TFunction, fullKey: string, fallback: string, values?: Record<string, string>): string {
  const translated = t(fullKey, { ...values, defaultValue: '' });
  return typeof translated === 'string' && translated.length > 0 ? translated : fallback;
}

/** Reads `multiInternetHud.<key>` from the LP namespace, falling back to the English HUD string. */
function translate(t: TFunction, key: string, fallback: string, values?: Record<string, string>): string {
  return read(t, `multiInternetHud.${key}`, fallback, values);
}

/** Reads `heroHud.<key>`, same fallback contract. */
function readHero(t: TFunction, key: string, fallback: string): string {
  return read(t, `heroHud.${key}`, fallback);
}

/** Reads `howItWorksHud.<key>`, same fallback contract. */
function readHowItWorks(t: TFunction, key: string, fallback: string, values?: Record<string, string>): string {
  return read(t, `howItWorksHud.${key}`, fallback, values);
}

/**
 * Builds the HUD strings for the Multi Internet instrument in the active locale.
 * Pure: takes `t`, returns a new object, touches no module state.
 */
export function buildMultiInternetHudStrings(t: TFunction): HudStrings {
  const base = HUD_STRINGS_EN;
  const english = base.multiInternetInstrument;

  const scenarios = SCENARIO_KEYS.reduce<typeof english.scenarios>(
    (accumulator, scenarioKey) => {
      const englishScenario = english.scenarios[scenarioKey];
      accumulator[scenarioKey] = {
        // The indicator badge is telemetry (`C INTERRUPTED`) and stays English.
        indicator: englishScenario.indicator,
        title: translate(t, `scenarios.${scenarioKey}.title`, englishScenario.title),
        detail: translate(t, `scenarios.${scenarioKey}.detail`, englishScenario.detail),
        aria: translate(t, `scenarios.${scenarioKey}.aria`, englishScenario.aria),
      };
      return accumulator;
    },
    { ...english.scenarios }
  );

  // `guideStages` is a fixed 3-tuple, so it is rebuilt positionally rather than
  // with `.map`, which would widen it to an array.
  const localiseStage = (stage: MultiInternetGuideStage, index: number): MultiInternetGuideStage => ({
    label: translate(t, `guideStages.${index}.label`, stage.label),
    action: translate(t, `guideStages.${index}.action`, stage.action),
  });
  const [firstStage, secondStage, thirdStage] = english.guideStages;
  const guideStages: HudStrings['multiInternetInstrument']['guideStages'] = [localiseStage(firstStage, 0), localiseStage(secondStage, 1), localiseStage(thirdStage, 2)];

  return {
    ...base,
    multiInternetInstrument: {
      ...english,
      netGroupLabel: (label) => translate(t, 'netGroupLabel', english.netGroupLabel(label), { label }),
      netOnlineLabel: (label) => translate(t, 'netOnlineLabel', english.netOnlineLabel(label), { label }),
      netInterruptedLabel: (label) => translate(t, 'netInterruptedLabel', english.netInterruptedLabel(label), { label }),
      bridgeGroupLabel: (server, location, netLabel) => translate(t, 'bridgeGroupLabel', english.bridgeGroupLabel(server, location, netLabel), { server, location, netLabel }),
      bandTitle: (label) => translate(t, 'bandTitle', english.bandTitle(label), { label }),
      computerGroupLabel: translate(t, 'computerGroupLabel', english.computerGroupLabel),
      computerTitle: translate(t, 'computerTitle', english.computerTitle),
      computerSub: translate(t, 'computerSub', english.computerSub),
      hubGroupLabel: translate(t, 'hubGroupLabel', english.hubGroupLabel),
      hubSub: translate(t, 'hubSub', english.hubSub),
      gameGroupLabel: translate(t, 'gameGroupLabel', english.gameGroupLabel),
      gameplayLabel: translate(t, 'gameplayLabel', english.gameplayLabel),
      systemStateFull: translate(t, 'systemStateFull', english.systemStateFull),
      systemStateDegraded: translate(t, 'systemStateDegraded', english.systemStateDegraded),
      reducedMotionAria: translate(t, 'reducedMotionAria', english.reducedMotionAria),
      scenarios,
      guideStages,
    },
  };
}

/**
 * Builds the HUD strings for the "3 Steps to Lower Ping" section in the active locale.
 *
 * Its animated status lines come from `HUD_STRINGS_EN` while the section's static JSX is
 * already translated through the namespace, so on a non-English page the two sat side by
 * side in different languages (SCRUM-1880). `common.installReady` and `common.optimized`
 * are read by this section alone, so overriding them here reaches nothing else.
 *
 * Translated is every status the section writes as language. Left in English are
 * `pingLockedFps` (`9MS PING · 84 FPS` is a readout, not a sentence) and the connection
 * badge's `online`/`offline`, which match the Multi Internet instrument's own telemetry
 * (`ONLINE · LIVE`) -- see the note at the top of this file.
 */
export function buildHowItWorksHudStrings(t: TFunction): HudStrings {
  const base = HUD_STRINGS_EN;
  const showcase = base.experienceShowcase;

  return {
    ...base,
    common: {
      ...base.common,
      installReady: readHowItWorks(t, 'installReady', base.common.installReady),
      optimized: readHowItWorks(t, 'optimized', base.common.optimized),
      optimizing: readHowItWorks(t, 'optimizing', base.common.optimizing),
      downloadingPercent: (percent) => readHowItWorks(t, 'downloadingPercent', base.common.downloadingPercent(percent), { percent: String(Math.round(percent)) }),
    },
    experienceShowcase: {
      ...showcase,
      readyToDownload: readHowItWorks(t, 'readyToDownload', showcase.readyToDownload),
      downloadStarted: readHowItWorks(t, 'downloadStarted', showcase.downloadStarted),
      pingOffline: readHowItWorks(t, 'pingOffline', showcase.pingOffline),
      pingOnline: readHowItWorks(t, 'pingOnline', showcase.pingOnline),
      loweringPingBoostingFps: readHowItWorks(t, 'loweringPingBoostingFps', showcase.loweringPingBoostingFps),
      gameQueued: (short) => readHowItWorks(t, 'gameQueued', showcase.gameQueued(short), { game: short }),
      gameSelected: (short) => readHowItWorks(t, 'gameSelected', showcase.gameSelected(short), { game: short }),
    },
  };
}

/**
 * Builds the HUD strings for the hero's live route diagnostic card in the active locale.
 *
 * The boundary here follows the elements, not the string list in the card (SCRUM-1882). The
 * chart's four labels and the callout that floats over the graph are annotation, and translate.
 * The status strip above the chart does not: `ROUTE LOCKED` shares that element with
 * `BASELINE DETECTED`, `TESTING 5 ROUTES`, `SMART REROUTE` and sits beside `8 ms / stable`, so
 * it reads as the instrument's readout -- the same call the Multi Internet telemetry follows.
 * Translating only part of a cycling element would leave it bilingual between states, which is
 * the defect this is meant to remove rather than move.
 *
 * The `ROUTE 1`-`ROUTE 5` chips stay English for the same reason as `ROUTE GROUP 0N`.
 */
export function buildHeroHudStrings(t: TFunction): HudStrings {
  const base = HUD_STRINGS_EN;
  const { hero } = base;

  return {
    ...base,
    hero: {
      ...hero,
      chartTitle: readHero(t, 'chartTitle', hero.chartTitle),
      chartEngine: readHero(t, 'chartEngine', hero.chartEngine),
      chartBefore: readHero(t, 'chartBefore', hero.chartBefore),
      chartAfter: readHero(t, 'chartAfter', hero.chartAfter),
      baselineCapture: readHero(t, 'baselineCapture', hero.baselineCapture),
      scanningRoutes: readHero(t, 'scanningRoutes', hero.scanningRoutes),
      reroutingPackets: readHero(t, 'reroutingPackets', hero.reroutingPackets),
      optimalRoute: readHero(t, 'optimalRoute', hero.optimalRoute),
    },
  };
}
